import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useFirestoreCollection } from "../firebase/listeners.js";

export type PackKind = "ruleset" | "scene";
export type PackTier =
  | { kind: "public" }
  | { kind: "guild"; guildId: string }
  | { kind: "user"; uid: string };

export interface PackListEntry {
  id: string;
  latestVersion: string;
  nameKey?: string;
  attribution?: string;
  sourceTier: PackTier;
}

interface Props {
  tier: PackTier;
  packKind: PackKind;
  onInstall: (entry: PackListEntry) => void | Promise<void>;
  onUninstall?: (entry: PackListEntry) => void | Promise<void>;
  /** Indicates packs already installed on the current table — renders Installed badge + Uninstall action. */
  installedIds?: Set<string>;
  /** Disable install buttons (e.g. while a write is in flight). */
  disabled?: boolean;
}

interface PackDoc {
  latestVersion?: string;
  nameKey?: string;
  attribution?: string;
}

function tierCollectionPath(tier: PackTier, kind: PackKind): string | null {
  // Firestore MVP schema only defines public-tier pack collections.
  if (tier.kind === "public") return `${kind}Packs`;
  return null;
}

export default function PackList({
  tier,
  packKind,
  onInstall,
  onUninstall,
  installedIds,
  disabled,
}: Props): ReactElement {
  const { t } = useTranslation();
  const { data, loading, error } = useFirestoreCollection<PackDoc>(
    tierCollectionPath(tier, packKind),
  );

  if (loading) return <p className="text-sm">{t("app.loading")}</p>;
  if (error) return <p className="text-sm text-destructive">{error.message}</p>;

  const entries: PackListEntry[] = data
    .map((row) => {
      if (typeof row.latestVersion !== "string") return null;
      return {
        id: row.id,
        latestVersion: row.latestVersion,
        ...(row.nameKey ? { nameKey: row.nameKey } : {}),
        ...(row.attribution ? { attribution: row.attribution } : {}),
        sourceTier: tier,
      } satisfies PackListEntry;
    })
    .filter((e): e is PackListEntry => e !== null);

  if (entries.length === 0) {
    return <p className="text-sm italic text-muted-foreground">{t("packs.empty")}</p>;
  }

  return (
    <ul className="space-y-2">
      {entries.map((e) => {
        const installed = installedIds?.has(e.id);
        return (
          <li key={e.id} className="flex items-start gap-3 rounded border p-2 text-sm">
            <div className="flex-1">
              <div className="font-medium">{e.nameKey ?? e.id}</div>
              <div className="text-xs text-muted-foreground">v{e.latestVersion}</div>
              {e.attribution && <div className="mt-1 text-xs italic">{e.attribution}</div>}
            </div>
            {installed ? (
              <>
                <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">
                  {t("packs.installed")}
                </span>
                {onUninstall && (
                  <button
                    type="button"
                    className="rounded border border-rose-400 px-2 py-0.5 text-xs text-rose-700 disabled:opacity-50"
                    disabled={disabled}
                    onClick={() => void onUninstall(e)}
                  >
                    {t("packs.uninstall")}
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                className="rounded bg-emerald-600 px-2 py-0.5 text-xs text-white disabled:opacity-50"
                disabled={disabled}
                onClick={() => void onInstall(e)}
              >
                {t("packs.install")}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
